import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useWorkOS } from './features/workos/context/WorkOSContext';
import Navbar from './components/layout/Navbar';
import Home from './features/static-pages/Home';
import Exchange from './features/static-pages/Exchange';
import Forum from './features/static-pages/Forum';
import Pricing from './features/pricing/pages/Pricing';
import Download from './features/static-pages/Download';
import TermsOfService from './features/static-pages/TermsOfService';
import PrivacyPolicy from './features/static-pages/PrivacyPolicy';
import Legal from './features/static-pages/Legal';
import Support from './features/static-pages/Support';
import Footer from './components/layout/Footer';
import ScrollToTop from './components/layout/ScrollToTop';
import LoadingSpinner from './components/common/LoadingSpinner';
import ProtectedRoute from './components/common/ProtectedRoute';
import WorkOSCallback from './features/workos/components/WorkOSCallback';
import Attributions from './features/static-pages/Attributions';
import InstitutionalInquiry from './features/static-pages/InstitutionalInquiry';
import JoinWaitlistForm from './features/static-pages/JoinWaitlistForm';
import MechInterpBannerPDF from './features/static-pages/MechInterpBannerPDF';
import HybridA5Banner from './features/static-pages/HybridA5Banner.jsx';
import PestIDA5Banner from './features/static-pages/PestIDA5Banner';
import ProjectGanttChart from './features/static-pages/ProjectGanttChart';
import AccountDashboard from './features/account/pages/AccountDashboard';
import ReleaseNotes from './features/static-pages/ReleaseNotes';
import Donate from './features/donations/pages/Donate';
import AdminDashboard from './features/admin/AdminDashboard';
import Docs from './features/docs/Docs';

const NRLBet = React.lazy(() => import('./features/nrlbet/pages/NRLBet'));

// Create an inner component that can use Router hooks
function AppContent() {
  const { isLoading } = useWorkOS();
  const location = useLocation();

  // Check if running in embedded mode (inside Electron app)
  const isEmbedded = new URLSearchParams(location.search).get('embedded') === 'true';
  const isFullscreen = location.pathname === '/nrlbet';

  // Show spinner immediately on callback route
  if (location.pathname === '/auth/callback') {
    return <LoadingSpinner />;
  }

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="min-h-screen bg-black">
      {!isEmbedded && !isFullscreen && <Navbar />}
      <main className={isEmbedded || isFullscreen ? '' : 'pt-28'}>
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<Home />} />
          <Route path="/exchange" element={<Exchange />} />
          <Route path="/exchange/:id" element={<Exchange />} />
          <Route path="/forum" element={<Forum />} />
          <Route path="/download" element={<Download />} />
          <Route path="/pricing" element={<Navigate to="/download" replace />} />
          <Route path="/subscription" element={<Navigate to="/download" replace />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/license" element={<Legal type="license" />} />
          <Route path="/support" element={<Support />} />
          <Route path="/docs" element={<Docs />} />
          <Route path="/donate" element={<Donate />} />
          <Route path="/institutional-inquiry" element={<InstitutionalInquiry />} />
          <Route path="/attributions" element={<Attributions />} />
          <Route path="/join-waitlist" element={<Navigate to="/subscription" replace />} />
          <Route path="/banner-pdf" element={<MechInterpBannerPDF />} />
          <Route path="/a5-banner" element={<HybridA5Banner />} />
          <Route path="/pestid-banner" element={<PestIDA5Banner />} />
          <Route path="/project-gantt" element={<ProjectGanttChart />} />
          <Route path="/release-notes" element={<ReleaseNotes />} />

          {/* WorkOS callback route */}
          <Route path="/auth/callback" element={<WorkOSCallback />} />

          {/* Protected routes */}
          <Route
            path="/account"
            element={<ProtectedRoute component={<AccountDashboard />} />}
          />
          <Route path="/admin" element={<AdminDashboard />} />
          <Route
            path="/nrlbet"
            element={<ProtectedRoute component={
              <React.Suspense fallback={<LoadingSpinner />}>
                <NRLBet />
              </React.Suspense>
            } />}
          />

          {/* Catch all route */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      {!isEmbedded && !isFullscreen && <Footer />}
      {!isEmbedded && !isFullscreen && <ScrollToTop />}
    </div>
  );
}

// Main App component with Router wrapper
function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;